import { Transaction } from '@mysten/sui/transactions';
import { SUI_SYSTEM_ADDRESS } from '@mysten/sui/utils';
import { appendConsolidateLooseCpcCoins, fetchLooseCpcCoins, } from './buildConsolidateCpc.js';
import { getCpcBalance } from './balance.js';
import { CPC_FRAMEWORK, DGC_TYPE } from './constants.js';
/** Minimum AB headroom when gas is also paid from address balance (no loose coins). */
export const CPC_STAKE_AB_GAS_RESERVE_MIST = 10000000n; // 0.01 DGC (same order as CLOB)
/**
 * Gas budget baked into stake PTBs (AB gas reserves the full budget until settle).
 * Must match {@link CPC_STAKE_AB_GAS_RESERVE_MIST} — was 10 DGC then 0.1; 0.01 covers stake + light consolidate.
 */
export const CPC_STAKE_GAS_BUDGET_MIST = CPC_STAKE_AB_GAS_RESERVE_MIST;
function appendStakeFromAddressBalance(tx, validatorAddress, amountMist) {
    const withdrawal = tx.withdrawal({ amount: amountMist, type: DGC_TYPE });
    const balance = tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::redeem_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [withdrawal],
    });
    const stakeCoin = tx.moveCall({
        target: `${CPC_FRAMEWORK}::coin::from_balance`,
        typeArguments: [DGC_TYPE],
        arguments: [balance],
    });
    tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::request_add_stake`,
        arguments: [tx.object.system(), stakeCoin, tx.pure.address(validatorAddress)],
    });
}
/**
 * Merge loose coins → AB when needed; pay gas from one loose coin (never send_funds that coin).
 * When AB already covers stake, keep the gas coin out of consolidation so AB withdrawal + gas do not race.
 */
function appendConsolidateLooseForStake(tx, owner, looseCoins, stakeMist, addressBalanceMist) {
    if (looseCoins.length === 0)
        return;
    if (addressBalanceMist >= stakeMist) {
        const [gasCoin, ...rest] = looseCoins;
        tx.setGasPayment([
            {
                objectId: gasCoin.coinObjectId,
                version: gasCoin.version,
                digest: gasCoin.digest,
            },
        ]);
        if (rest.length > 0) {
            appendConsolidateLooseCpcCoins(tx, owner, rest.map((c) => c.coinObjectId));
        }
        return;
    }
    if (looseCoins.length >= 2) {
        const [gasCoin, ...toMerge] = looseCoins;
        tx.setGasPayment([
            {
                objectId: gasCoin.coinObjectId,
                version: gasCoin.version,
                digest: gasCoin.digest,
            },
        ]);
        appendConsolidateLooseCpcCoins(tx, owner, toMerge.map((c) => c.coinObjectId));
        return;
    }
    // Single loose coin: merge into AB, pay gas from AB (same PTB).
    appendConsolidateLooseCpcCoins(tx, owner, [looseCoins[0].coinObjectId]);
    tx.setGasPayment([]);
}
/**
 * Stake from address balance (P-UX): optional merge loose coins → AB, then withdrawal → stake.
 * When loose coins exist, gas is paid from a coin object (same PTB) so MAX stake does not
 * compete with address-balance gas reservation.
 */
export function buildStakeCpcFromAddressBalance({ validatorAddress, amountMist, looseCoins = [], owner, addressBalanceMist = 0n, }) {
    const tx = new Transaction();
    const amount = BigInt(amountMist);
    if (looseCoins.length > 0) {
        appendConsolidateLooseForStake(tx, owner, looseCoins, amount, addressBalanceMist);
        tx.setGasBudgetIfNotSet(CPC_STAKE_GAS_BUDGET_MIST);
    }
    else {
        tx.setGasPayment([]);
        tx.setGasBudget(CPC_STAKE_GAS_BUDGET_MIST);
    }
    appendStakeFromAddressBalance(tx, validatorAddress, amount);
    return tx;
}
/** @deprecated P-UX: use {@link buildStakeCpcForOwner} (address balance, no split). */
export function buildStakeCpcFromCoin() {
    throw new Error('buildStakeCpcFromCoin is disabled under P-UX; use buildStakeCpcForOwner');
}
/** @deprecated P-UX: use {@link buildStakeCpcForOwner}. */
export function buildStakeCpc() {
    throw new Error('buildStakeCpc is disabled under P-UX; use buildStakeCpcForOwner');
}
/** Max stake mist for UI: gas from a loose coin when possible; else reserve AB gas headroom. */
export function maxStakeMistFromBalance(bal) {
    if (bal.totalMist <= 0n)
        return 0n;
    const hasLooseCoinForGas = bal.looseCoinMist > 0n && (bal.coinObjectCount ?? 0) > 0;
    if (hasLooseCoinForGas) {
        if (bal.addressBalanceMist > 0n) {
            return bal.addressBalanceMist;
        }
        if (bal.looseCoinMist <= CPC_STAKE_AB_GAS_RESERVE_MIST)
            return 0n;
        return bal.looseCoinMist - CPC_STAKE_AB_GAS_RESERVE_MIST;
    }
    if (bal.addressBalanceMist <= CPC_STAKE_AB_GAS_RESERVE_MIST)
        return 0n;
    return bal.addressBalanceMist - CPC_STAKE_AB_GAS_RESERVE_MIST;
}
/**
 * P-UX stake: merge any loose `Coin<DGC>` into AB, then stake from AB only (no split).
 */
export async function buildStakeCpcForOwner(client, owner, params) {
    const amount = BigInt(params.amountMist);
    const bal = await getCpcBalance(client, owner);
    if (bal.addressBalanceMist + bal.looseCoinMist < amount) {
        throw new Error(`Insufficient DGC for stake: need ${amount} mist; address balance ${bal.addressBalanceMist}, loose coins ${bal.looseCoinMist}`);
    }
    const maxStake = maxStakeMistFromBalance(bal);
    if (amount > maxStake) {
        throw new Error(bal.looseCoinMist > 0n
            ? `Stake amount too high: max ${maxStake} mist with current address balance / loose coins`
            : `Stake amount too high: leave at least ${CPC_STAKE_AB_GAS_RESERVE_MIST} mist in address balance for gas`);
    }
    const looseCoins = await fetchLooseCpcCoins(client, owner);
    return buildStakeCpcFromAddressBalance({
        ...params,
        owner,
        looseCoins,
        addressBalanceMist: bal.addressBalanceMist,
    });
}
